// Leaving a page with unsaved changes asks first (02-DESIGN/03-console/01-the-console.md
// § the blueprint's page). The console's router is not a data router, so
// while there are changes the guard holds every navigation the router makes
// to another address — a link, a table's row, a page's own move — and asks
// in the console's confirmation, replaying the same navigation, its state
// included, when the person leaves. A move that is not the router's — the
// workspace menu's pick, which changes the session first — asks through
// whenLeaving. The browser asks its own question for a reload, a closed tab
// or another site. The browser's back button is not held.

import { useContext, useEffect, useState, type ReactNode } from "react";
import { parsePath, UNSAFE_NavigationContext, type Navigator, type To } from "react-router";

import { ConfirmDialog } from "../../ui/table.tsx";

/** The router's own push and replace, kept once per navigator so a guard never wraps another's wrapper. */
const originals = new WeakMap<Navigator, Pick<Navigator, "push" | "replace">>();

/** The guard asking now, if a page with unsaved changes is open: one at a time. */
let asking: ((go: () => void) => void) | undefined;

/**
 * whenLeaving runs a move off the page: at once where nothing is unsaved,
 * and after the person confirms where something is.
 */
export function whenLeaving(go: () => void): void {
  if (asking === undefined) {
    go();
    return;
  }
  asking(go);
}

/** leaves reports whether a navigation goes to another address than `here`: a form opened on the same one does not. */
export function leaves(to: To, here: string): boolean {
  const path = typeof to === "string" ? parsePath(to) : to;
  return path.pathname !== undefined && path.pathname !== "" && path.pathname !== here;
}

/**
 * hold puts the guard in front of the router's push and replace: a
 * navigation to another address waits on `ask`, one on the same address — a
 * form opened — and one the person already confirmed (`passing`) go
 * through. It returns what puts the router's own back.
 */
function hold(navigator: Navigator, ask: (go: () => void) => void, passing: () => boolean): () => void {
  const base = originals.get(navigator) ?? {
    push: navigator.push.bind(navigator),
    replace: navigator.replace.bind(navigator),
  };
  originals.set(navigator, base);
  const { push, replace } = base;
  navigator.push = (to, state, opts) => {
    if (passing() || !leaves(to, window.location.pathname)) {
      push(to, state, opts);
      return;
    }
    ask(() => {
      push(to, state, opts);
    });
  };
  navigator.replace = (to, state, opts) => {
    if (passing() || !leaves(to, window.location.pathname)) {
      replace(to, state, opts);
      return;
    }
    ask(() => {
      replace(to, state, opts);
    });
  };
  return () => {
    navigator.push = push;
    navigator.replace = replace;
  };
}

/** useAskBeforeLeaving returns the confirmation to draw while a leave waits on the person. */
export function useAskBeforeLeaving(dirty: boolean, consequence: string): ReactNode {
  const [held, setHeld] = useState<{ go: () => void } | undefined>(undefined);
  const { navigator } = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    if (!dirty) {
      return;
    }
    let passing = false;
    const ask = (go: () => void) => {
      setHeld({
        go: () => {
          passing = true;
          try {
            go();
          } finally {
            passing = false;
          }
        },
      });
    };
    const release = hold(navigator, ask, () => passing);
    asking = ask;
    const unload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", unload);
    return () => {
      release();
      if (asking === ask) {
        asking = undefined;
      }
      window.removeEventListener("beforeunload", unload);
    };
  }, [dirty, navigator]);
  if (held === undefined) {
    return null;
  }
  return (
    <ConfirmDialog
      ask={{ title: "Leave without saving?", consequence }}
      verb="Leave"
      onConfirm={() => {
        setHeld(undefined);
        held.go();
      }}
      onCancel={() => {
        setHeld(undefined);
      }}
    />
  );
}
